<template>
  <div class="b365-page">
    <header class="b365-hero">
      <div>
        <div class="b365-kicker">BET365 CONTROL PANEL</div>
        <h1>Accounts Matrix</h1>
        <p>High-clarity view for credentials, geo mapping, payout fields, and activation state.</p>
      </div>
      <a class="b365-back" href="/apps/script-tags/data-manager">Back to data manager</a>
    </header>

    <section class="b365-board">
      <form @submit="save">
        <div class="b365-grid b365-grid--head">
          <div>On/Off</div>
          <div>ID</div>
          <div>User</div>
          <div>Password</div>
          <div>Account</div>
          <div>Geo</div>
          <div>iOS PO</div>
          <div>Android Scraper</div>
          <div>Android PO</div>
          <div>Actions</div>
        </div>

        <div
          v-for="row of rows"
          :key="row.id"
          class="b365-grid b365-grid--row"
          :class="{ 'is-editing': row.edit }"
        >
          <div>
            <b-form-checkbox switch v-model="row.is_active" :disabled="!row.edit || !userHasPermission('admin')"></b-form-checkbox>
          </div>
          <div><span class="b365-id">#{{ row.id }}</span></div>

          <div>
            <div v-if="!row.edit" class="b365-mono">{{ row.username }}</div>
            <b-form-input v-else class="b365-input" type="text" v-model.trim="row.username" placeholder="Username" />
          </div>

          <div>
            <div v-if="!row.edit" class="b365-mono">{{ row.password }}</div>
            <b-form-input v-else class="b365-input" type="text" v-model.trim="row.password" placeholder="Password" />
          </div>

          <div>
            <div v-if="!row.edit">{{ row.account }}</div>
            <b-form-input
              v-else
              class="b365-input"
              :disabled="!userHasPermission('admin')"
              type="text"
              v-model.trim="row.account"
              placeholder="Account"
            />
          </div>

          <div>
            <div v-if="!row.edit">{{ row.geo }}</div>
            <div v-else>
              <cool-select v-model="geoSelected" :items="geosSelect" text="label" value="value" />
              <ul class="selected-items" v-if="row.geo">
                <li>
                  <i class="fa fa-times del-x" @click="removeGeo(row)" aria-hidden="true"></i>
                  <span>{{ geoSelected || row.geo }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <div v-if="!row.edit">{{ row.ios_po }}</div>
            <b-form-input
              v-else
              class="b365-input"
              :disabled="!userHasPermission('admin')"
              type="text"
              v-model.trim="row.ios_po"
              placeholder="iOS PO"
            />
          </div>

          <div>
            <div v-if="!row.edit" class="b365-check">{{ row.android_scraper ? "✓" : "☐" }}</div>
            <b-form-checkbox v-else v-model="row.android_scraper" :disabled="!userHasPermission('admin')"></b-form-checkbox>
          </div>

          <div>
            <div v-if="!row.edit">{{ row.android_po ?? '' }}</div>
            <b-form-input
              v-else
              class="b365-input"
              :disabled="!userHasPermission('admin')"
              type="text"
              v-model.trim="row.android_po"
              placeholder="Android PO (optional)"
            />
          </div>

          <div class="b365-actions">
            <template v-if="!row.edit">
              <button v-if="isAdmin" class="b365-btn b365-btn--neutral" type="button" @click="editRow(row.id)">Edit</button>
              <button v-if="isAdmin" class="b365-btn b365-btn--danger" type="button" @click="deleteEntity(row.id)">Delete</button>
            </template>
            <template v-else>
              <button class="b365-btn b365-btn--ok" type="button" @click="saveEditRow(row.id)">Save</button>
              <button class="b365-btn b365-btn--neutral" type="button" @click="cancelEditRow(row.id)">Cancel</button>
            </template>
          </div>
        </div>

        <div v-if="isAdmin && showNewRow" class="b365-grid b365-grid--row b365-grid--new">
          <div><b-form-checkbox switch v-model="newRow.is_active" :disabled="!userHasPermission('admin')"></b-form-checkbox></div>
          <div><span class="b365-id">NEW</span></div>
          <div><b-form-input class="b365-input" type="text" v-model="newRow.username" placeholder="Username" /></div>
          <div><b-form-input class="b365-input" type="text" v-model="newRow.password" placeholder="Password" /></div>
          <div><b-form-input class="b365-input" :disabled="!userHasPermission('admin')" type="text" v-model="newRow.account" placeholder="Account" /></div>
          <div><cool-select v-model="newUserGeoSelected" :items="geosSelect" text="label" placeholder="Geo" /></div>
          <div><b-form-input class="b365-input" :disabled="!userHasPermission('admin')" type="text" v-model="newRow.ios_po" placeholder="iOS PO" /></div>
          <div><b-form-checkbox v-model="newRow.android_scraper" :disabled="!userHasPermission('admin')"></b-form-checkbox></div>
          <div><b-form-input class="b365-input" :disabled="!userHasPermission('admin')" type="text" v-model="newRow.android_po" placeholder="Android PO (optional)" /></div>
          <div class="b365-actions"><button class="b365-btn b365-btn--ok" type="submit">Add</button></div>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import countries from '@/assets/countries.js';
import { CoolSelect } from 'vue-cool-select';
import 'vue-cool-select/dist/themes/bootstrap.css';

export default {
  name: 'Bet365Accounts',
  components: {
    CoolSelect
  },
  data() {
    return {
      all_geos: [],
      newRow: {
        id: 0,
        username: '',
        password: '',
        account: '',
        geo: '',
        ios_po: '',
        android_po: null,
        android_scraper: false,
        is_active: true
      },
      rows: [],
      showNewRow: true,
      isAdmin: true,
      sortOptions: {
        sortOrder: 'desc',
        sortByField: 'pid_text'
      },
      geoSelected: null,
      newUserGeoSelected: null,
      labelRelatedToGeo: null
    };
  },
  created() {
    this.getListData();

    const geoSelect = Object.keys(countries).map(code => ({
      label: countries[code],
      value: code
    }));
    this.all_geos = geoSelect;
  },
  computed: {
    geosSelect() {
      return Object.values(countries);
    }

  },
  methods: {
    async save(e) {
      e.preventDefault();
      const newUser = { ...this.newRow };
      const countryCode = Object.keys(countries).find(key => countries[key] === this.newUserGeoSelected);
      newUser.geo = countryCode;

      const errors = await this.validateRowData(newUser);
      if (errors.length > 0) {
        this.notifyError("Failure reasons: " + errors.join(", "));
        return;
      }

      newUser.ios_po = parseFloat(newUser.ios_po);
      if (newUser.android_po === '') newUser.android_po = null;
      try {
        const data = await this.$http.post(this.resources.Management.saveBet365User, newUser);
        if (data.body.res !== "OK") {
          throw new Error()
        } else {
          this.newRow = {
            id: 0,
            username: '',
            password: '',
            account: '',
            geo: '',
            ios_po: '',
            android_po: null,
            android_scraper: false,
            is_active: true
          };
          this.newUserGeoSelected = null;
          this.geoSelected = null;
          this.getListData();
        }
      } catch (err) {
        this.notifyError("Error saving new User");
      }

    },
    async getListData() {
      const data = await this.$http.post(this.resources.Management.getBet365Users, {});
      for (let k in data.body.data) {
        data.body.data[k].edit = false;
        if (data.body.data[k].android_scraper === undefined) data.body.data[k].android_scraper = false;
        if (data.body.data[k].android_po === undefined) data.body.data[k].android_po = null;
        if (data.body.data[k].is_active === undefined) data.body.data[k].is_active = true;
      }
      this.rows = data.body.data;
    },
    editRow(id) {
      this.showNewRow = false;
      this.geoSelected = null;
      this.rows.forEach(row => {
        if (row.id === id) {
          row.edit = true;
          this.geoSelected = row.geo;
        } else {
          row.edit = false;
        }
      });
      this.newUserGeoSelected = null;
    },
    async saveEditRow(id) {
      let row = this.rows.find(r => r.id === id);
      if (!row) return;
      const copy = {
        username: row.username,
        password: row.password,
        account: row.account,
        ios_po: row.ios_po,
        android_po: row.android_po === '' ? null : row.android_po,
        android_scraper: !!row.android_scraper,
        is_active: !!row.is_active,
        id: row.id,
      };

      const isValidCode = countries[this.geoSelected] !== undefined;
      
      copy.geo = isValidCode
        ? this.geoSelected
        : Object.keys(countries).find(key => countries[key] === this.geoSelected);

      const errors = await this.validateRowData(copy);
      if (errors.length > 0) {
        this.notifyError("Failure reasons: " + errors.join(", "));
        return;
      }
      copy.ios_po = parseFloat(copy.ios_po);
      row.edit = false;

      try {
        const data = await this.$http.post(this.resources.Management.saveBet365User, copy);
        if (data.body.res !== "OK") throw new Error();
      } catch (err) {
        this.notifyError("Error editing user");
      }

      this.geoSelected = null;
      this.showNewRow = true;
      this.getListData();

    },
    async deleteEntity(id) {
      try {
        const data = await this.$http.post(this.resources.Management.deleteBet365User, { id });
        if (data.body.res !== "OK") throw new Error();
      } catch (err) {
        this.notifyError("Error deleting entity");
      }
      this.getListData();
    },
    async validateRowData(user) {
      let errors = [];
      const requiredKeys = ["username", "password", "account", "geo", "ios_po"];
      for (const k of requiredKeys) {
        if (user[k] === undefined || user[k] === null || user[k] === "") {
          errors.push("All fields are required (except Android PO)");
          break;
        }
      }

      const noSpacesKeys = ["username", "password", "geo"];
      for (const k of noSpacesKeys) {
        const v = user[k];
        if (typeof v === "string" && v.includes(" ")) {
          errors.push("No white spaces allowed");
          break;
        }
      }

      if (user.account !== undefined && user.account !== null && user.account !== "" && typeof user.account === "string" && user.account.includes("  ")) {
        errors.push("Account contains invalid spacing");
      }

      if (user.ios_po !== undefined && user.ios_po !== null && user.ios_po !== "" && isNaN(Number(user.ios_po))) {
        errors.push("iOS PO should be a number");
      }

      if (user.android_po !== undefined && user.android_po !== null && user.android_po !== "") {
        if (typeof user.android_po !== "string" && typeof user.android_po !== "number") {
          errors.push("Android PO should be a number or empty");
        } else if (isNaN(Number(user.android_po))) {
          errors.push("Android PO should be a number or empty");
        }
      }

      return errors;
    },
    // onGeoSelected(row) {
    //   const selected = this.all_geos.find(g => g.label === row.geoSelected);
    //   if (selected) {
    //     row.geo = selected.label;
    //   }
    // },
    removeGeo(row) {
      row.geo = '';
      // row.geoSelected = null;
    },
    userHasPermission(role) {
      // Replace with actual permission logic
      return this.isAdmin;
    },
    notifyError(msg) {
      alert(msg); // Replace with actual toast/notify
    },
    getCountryLabel(code) {
      const match = Object.values(countries)
      return match ? match.label : code;
    },
    cancelEditRow(id) {
      this.showNewRow = true;
      this.rows.forEach(row => {
        if (row.id === id) {
          row.edit = false;
          this.geoSelected = null;
        }
      });
    }
  }
};
</script>

<style scoped>
.b365-page {
  min-height: 100%;
  padding: 24px;
  color: #0f172a;
  background:
    radial-gradient(900px 480px at 0% -15%, rgba(34, 211, 238, 0.20), transparent 60%),
    radial-gradient(980px 540px at 100% -18%, rgba(168, 85, 247, 0.16), transparent 62%),
    linear-gradient(130deg, #f8fafc 0%, #f1f5f9 55%, #eef2ff 100%);
}

.b365-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 18px;
}

.b365-kicker {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .5px;
  background: rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(15, 23, 42, 0.12);
}

.b365-hero h1 {
  margin: 8px 0 4px;
  font-size: 32px;
  line-height: 1.05;
  font-weight: 800;
}

.b365-hero p {
  margin: 0;
  color: rgba(15, 23, 42, 0.70);
}

.b365-back {
  color: #0ea5e9;
  text-decoration: none;
  border: 1px solid rgba(14, 165, 233, 0.28);
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(14, 165, 233, 0.08);
}

.b365-board {
  border-radius: 20px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(15, 23, 42, 0.10);
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.10);
  backdrop-filter: blur(10px);
}

.b365-grid {
  display: grid;
  grid-template-columns: 90px 76px 180px 180px 180px 140px 120px 170px 170px 200px;
  column-gap: 24px;
  align-items: center;
}

.b365-grid--head {
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: .4px;
  text-transform: uppercase;
  padding: 8px 14px 12px;
}

.b365-grid--row {
  margin-bottom: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.b365-grid--row:hover {
  border-color: rgba(14, 165, 233, 0.35);
  box-shadow: 0 14px 32px rgba(14, 165, 233, 0.10);
}

.b365-grid--row.is-editing {
  border-color: rgba(14, 165, 233, 0.65);
  background: rgba(240, 249, 255, 0.95);
  box-shadow: 0 0 0 1px rgba(14, 165, 233, 0.18) inset, 0 16px 34px rgba(14, 165, 233, 0.12);
}

.b365-grid--new {
  border-style: dashed;
  border-color: rgba(34, 197, 94, 0.45);
  background: rgba(240, 253, 244, 0.92);
}

.b365-id {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.06);
}

.b365-check {
  font-size: 18px;
  font-weight: 700;
}

.b365-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.b365-actions {
  display: flex;
  gap: 8px;
}

.b365-btn {
  border: 0;
  border-radius: 10px;
  padding: 7px 12px;
  font-weight: 700;
  color: #fff;
}

.b365-btn--ok {
  background: linear-gradient(135deg, #10b981, #22c55e);
}

.b365-btn--neutral {
  background: linear-gradient(135deg, #64748b, #475569);
}

.b365-btn--danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

:deep(.b365-input.form-control) {
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 1);
}

:deep(.b365-input.form-control:focus) {
  border-color: rgba(14, 165, 233, 0.85);
  box-shadow: 0 0 0 0.18rem rgba(14, 165, 233, 0.22);
}

:deep(.custom-control-input:checked ~ .custom-control-label::before) {
  background-color: #10b981;
  border-color: #10b981;
}

@media (max-width: 1700px) {
  .b365-board {
    overflow-x: auto;
  }
  .b365-grid {
    min-width: 1650px;
  }
}
</style>
